import React from 'react';
import { Avatar, AvatarProps } from '../avatar/avatar';
import {
  FlexColumn,
  FlexRow,
  FlexRowProps,
  HashLength,
  Size,
  TruncateBox,
} from '@make-software/cspr-design';
import { BodyText } from '@block-explorer/ui';
import { isValidPublicKey } from '../../utils/account';
import { useTranslation } from 'react-i18next';
import { HashLink } from '../hash-link/hash-link';
import {
  ACCOUNT_DETAILS_PATH,
  RouterPathsMap,
  VALIDATOR_DETAILS_PATH,
} from '../../router/paths';
import { buildRouterPath } from '../../router/utils';
import styled from 'styled-components';
import TooltipWithExtendedInfo from '../tooltip-with-extended-info/tooltip-with-extended-info';

interface TableDataAccountInfoProps {
  logo: string | null;
  name: string | undefined;
  loading?: boolean;
  csprName?: string | undefined;
  publicKey: string | null | undefined;
  tooltipCaption?: string;
  navigationPath?: keyof RouterPathsMap;
  hashLength?: HashLength;
  validatorAccount?: boolean;
  nameSize?: Size;
  avatarSize?: AvatarProps['size'];
  hashFontSize?: HashFontSize;
  copyNotifyingStyle?: 'full' | 'tiny';
  horizontalAlign?: FlexRowProps['align'];
}

const StyledTruncateBox = styled(TruncateBox)(() => ({
  height: '20px',
}));

export enum HashFontSize {
  'default' = 'default',
  'big' = 'big',
}

const StyledHashWrapper = ({ hashFontSize, ...props }) => {
  return hashFontSize === HashFontSize.big ? (
    <BodyText size={3} scale="sm" {...props} />
  ) : (
    <BodyText size={3} {...props} />
  );
};

// TODO: Refactor this, using Address component
// Check usage of this component
export const TableDataAccountInfo = ({
  publicKey,
  csprName,
  logo,
  name,
  loading,
  hashLength,
  validatorAccount,
  copyNotifyingStyle,
  navigationPath = ACCOUNT_DETAILS_PATH,
  tooltipCaption,
  nameSize = 5,
  avatarSize = 'default',
  hashFontSize = HashFontSize.default,
  horizontalAlign = 'center',
}: TableDataAccountInfoProps) => {
  const { t } = useTranslation();

  const targetPath = validatorAccount ? VALIDATOR_DETAILS_PATH : navigationPath;

  if (loading || !publicKey) {
    return (
      <FlexRow align={horizontalAlign} itemsSpacing={12}>
        <Avatar hash={publicKey} loading={loading} size={avatarSize} />
      </FlexRow>
    );
  }

  if (publicKey === '00') {
    // publicKey == '00' means that it is a Immediate Switch Block
    // NOTE: as part of Casper network node v1.5, the node software now creates an "immediate switch block" on upgrades;
    // there are no rewards for this block. it simply captures the information after application of the upgrade,
    // which allows this to be deterministically detected
    return (
      <FlexRow align={horizontalAlign} itemsSpacing={12}>
        <Avatar hash={publicKey} loading={loading} size={avatarSize} />
        <FlexColumn>
          <BodyText size={2} monotype>
            {publicKey}
          </BodyText>
          <BodyText size={3} variation="darkGray" noWrap>
            System
          </BodyText>
        </FlexColumn>
      </FlexRow>
    );
  }

  const path = buildRouterPath(targetPath, { id: publicKey }, undefined);
  const keyTooltipCaption = tooltipCaption
    ? tooltipCaption
    : isValidPublicKey(publicKey)
      ? t('Public Key')
      : t('Account hash');

  return (
    <FlexRow align={horizontalAlign} itemsSpacing={12}>
      {logo ? (
        <Avatar
          src={logo}
          loading={loading}
          size={avatarSize}
          alt={'Account logo'}
        />
      ) : (
        <Avatar hash={publicKey} loading={loading} size={avatarSize} />
      )}

      <TooltipWithExtendedInfo
        extendedLine={{ title: csprName, caption: t('CSPR.name') }}
        tooltipCaption={keyTooltipCaption}
        hash={publicKey}
      >
        <FlexColumn>
          {name ? (
            <>
              <StyledHashWrapper
                hashFontSize={hashFontSize}
                monotype={!csprName}
              >
                <HashLink
                  copyNotifyingStyle={copyNotifyingStyle}
                  href={path}
                  hash={publicKey}
                  csprName={csprName}
                  hashLength={hashLength}
                  ariaDescription={`Link to ${
                    validatorAccount ? 'Validators page' : 'Account page'
                  }`}
                />
              </StyledHashWrapper>
              <FlexRow itemsSpacing={6} align={'center'}>
                <StyledTruncateBox size={nameSize}>
                  <BodyText size={3} variation="darkGray" noWrap>
                    {name}
                  </BodyText>
                </StyledTruncateBox>
              </FlexRow>
            </>
          ) : (
            <StyledHashWrapper hashFontSize={hashFontSize} monotype={!csprName}>
              <HashLink
                href={path}
                hash={publicKey}
                csprName={csprName}
                hashLength={hashLength}
                copyNotifyingStyle={copyNotifyingStyle}
                ariaDescription={`Link to ${
                  validatorAccount ? 'Validators page' : 'Account page'
                }`}
                horizontalAlign={horizontalAlign}
              />
            </StyledHashWrapper>
          )}
        </FlexColumn>
      </TooltipWithExtendedInfo>
    </FlexRow>
  );
};
